import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { darkTheme, lightTheme } from '@/theme/muiTheme';

type ThemeMode = 'light' | 'dark' | 'auto';

interface ThemeStore {
  // State
  themeMode: ThemeMode;
  isDark: boolean;
  hasUserOverride: boolean; // Track if user has manually changed theme

  // Actions
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void; // Simple toggle between light/dark
  initialize: () => void;
}

// Helper functions
const getSystemPreference = (): boolean => {
  try {
    return !!window.matchMedia('(prefers-color-scheme: dark)')?.matches;
  } catch {
    // Fallback to light mode if matchMedia is not available
    return false;
  }
};

const calculateIsDark = (mode: ThemeMode): boolean => {
  switch (mode) {
    case 'light':
      return false;
    case 'dark':
      return true;
    case 'auto':
      return getSystemPreference();
    default:
      return false;
  }
};

const applyTheme = (isDark: boolean) => {
  try {
    const root = document.documentElement;
    if (!root) return; // Exit early if documentElement is not available

    const theme = isDark ? darkTheme : lightTheme;

    // Set data-theme attribute for CSS custom properties
    root.setAttribute('data-theme', isDark ? 'dark' : 'light');

    // Apply CSS custom properties for consistent theming using theme colors
    root.style.setProperty('--app-bg', theme.palette.background.default);
    root.style.setProperty('--app-text', theme.palette.text.primary);
    root.style.setProperty('--app-border', theme.palette.divider);
    root.style.setProperty('--app-primary', theme.palette.primary.main);
    root.style.setProperty('--app-primary-hover-bg', isDark ? 'rgba(245, 158, 11, 0.08)' : 'rgba(146, 64, 14, 0.04)');

    // Google Maps specific custom properties
    root.style.setProperty('--maps-info-window-bg', isDark ? '#3a3a3a' : theme.palette.background.paper);
    root.style.setProperty('--maps-info-window-text', theme.palette.text.primary);
    root.style.setProperty('--maps-info-window-border', theme.palette.divider);
    root.style.setProperty('--maps-close-button-color', isDark ? 'rgba(201, 180, 180, 0.36)' : 'rgba(0, 0, 0, 0.1)');
    root.style.setProperty('--maps-close-button-hover', isDark ? 'rgb(233, 66, 66)' : 'rgba(0, 0, 0, 0.1)');
    root.style.setProperty('--button-hover-bg', isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)');

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDark ? theme.palette.background.default : theme.palette.primary.main);
    }
  } catch (error) {
    // Silently fail if DOM manipulation is not available
    console.warn('Theme application failed:', error);
  }
};

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      // Initial state
      themeMode: 'auto', // Default to auto (system preference)
      isDark: false,
      hasUserOverride: false, // Track if user has manually changed theme

      // Set theme mode
      setThemeMode: (mode: ThemeMode) => {
        const isDark = calculateIsDark(mode);

        set({
          themeMode: mode,
          isDark,
          hasUserOverride: mode !== 'auto', // Mark as user override if not auto
        });
        applyTheme(isDark);
      },

      // Simple toggle between light and dark
      toggleTheme: () => {
        const { isDark } = get();
        const newMode: ThemeMode = isDark ? 'light' : 'dark';
        const newIsDark = calculateIsDark(newMode);

        set({
          themeMode: newMode,
          isDark: newIsDark,
          hasUserOverride: true, // Mark as user override
        });
        applyTheme(newIsDark);
      },

      // Initialize theme
      initialize: () => {
        const { themeMode, hasUserOverride } = get();

        // If no user override, use system preference
        const effectiveMode = hasUserOverride ? themeMode : 'auto';
        const isDark = calculateIsDark(effectiveMode);

        set({
          themeMode: effectiveMode,
          isDark,
        });
        applyTheme(isDark);
      },
    }),
    {
      name: 'coffee-app-theme', // localStorage key
      partialize: (state) => ({
        themeMode: state.themeMode,
        hasUserOverride: state.hasUserOverride,
      }), // Persist both themeMode and hasUserOverride
    },
  ),
);

// System theme change listener - only applies if user hasn't overridden
try {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleSystemThemeChange = () => {
    const { themeMode, hasUserOverride, setThemeMode } = useThemeStore.getState();

    // Only respond to system changes if user hasn't manually overridden and we're in auto mode
    if (!hasUserOverride || themeMode === 'auto') {
      setThemeMode('auto'); // Re-trigger to update isDark based on new system preference
    }
  };

  mediaQuery.addEventListener('change', handleSystemThemeChange);
} catch {
  // Silently fail if matchMedia is not available
}

// Initialize on module load
useThemeStore.getState().initialize();
